import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import axiosInstance from '../../utils/axios';
import {
  AdminEmptyState,
  AdminFilterBar,
  AdminPageHeader,
  useAdminFeedback,
} from '../../components/admin/primitives';
import {
  bulkSetDivisionInsurance,
  listDivisionTrucks,
  listDriverPasses,
  listFleetInsurance,
  removeDriverPass,
  removeFleetInsurance,
  setDriverPass,
  setFleetInsurance,
} from '../../services/adminPassesInsuranceService';
import ridersService from '../../services/ridersService';

const TABS = ['driver-passes', 'fleet-insurance'];

const PASS_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'valid', label: 'Valid' },
  { value: 'expired', label: 'Expired' },
  { value: 'none', label: 'No pass' },
];

const INS_STATUS_OPTIONS = [
  { value: 'all', label: 'All insured' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'none', label: 'Uninsured trucks' },
];

function statusChip(status) {
  if (status === 'valid' || status === 'active') return <Chip size="small" label={status} color="success" />;
  if (status === 'expired') return <Chip size="small" label={status} color="error" />;
  return <Chip size="small" label="none" color="default" variant="outlined" />;
}

function toDateInputValue(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function isValidObjectId(value) {
  if (value == null || value === '') return false;
  const s = String(value).trim();
  return s !== 'undefined' && s !== 'null' && /^[a-f0-9]{24}$/i.test(s);
}

export default function AdminPassesInsurance() {
  const { showSuccess, showError, Feedback } = useAdminFeedback();
  const [tab, setTab] = useState(0);

  const [divisions, setDivisions] = useState([]);
  const [passTypes, setPassTypes] = useState([]);
  const [insuranceTiers, setInsuranceTiers] = useState([]);

  const [passItems, setPassItems] = useState([]);
  const [passPage, setPassPage] = useState(1);
  const [passPages, setPassPages] = useState(1);
  const [passStatus, setPassStatus] = useState('all');
  const [passSearch, setPassSearch] = useState('');
  const [passDivisionId, setPassDivisionId] = useState('');
  const [passLoading, setPassLoading] = useState(false);

  const [insItems, setInsItems] = useState([]);
  const [insPage, setInsPage] = useState(1);
  const [insPages, setInsPages] = useState(1);
  const [insStatus, setInsStatus] = useState('all');
  const [insSearch, setInsSearch] = useState('');
  const [insDivisionId, setInsDivisionId] = useState('');
  const [insLoading, setInsLoading] = useState(false);

  const [passDialog, setPassDialog] = useState(null);
  const [passRiderOptions, setPassRiderOptions] = useState([]);
  const [insDialog, setInsDialog] = useState(null);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDivisions = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/divisions');
      const list = data?.divisions ?? data?.items ?? data;
      setDivisions(Array.isArray(list) ? list : []);
    } catch {
      setDivisions([]);
    }
  }, []);

  const loadPasses = useCallback(async (page = passPage) => {
    setPassLoading(true);
    try {
      const data = await listDriverPasses({
        page,
        limit: 25,
        status: passStatus,
        search: passSearch || undefined,
        divisionId: passDivisionId || undefined,
      });
      setPassItems(data.items || []);
      setPassPages(data.pagination?.totalPages || 1);
      setPassPage(data.pagination?.page || page);
      if (data.passTypes?.length) setPassTypes(data.passTypes);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to load driver passes');
    } finally {
      setPassLoading(false);
    }
  }, [passPage, passStatus, passSearch, passDivisionId, showError]);

  const loadInsurance = useCallback(async (page = insPage) => {
    setInsLoading(true);
    try {
      const uninsuredOnly = insStatus === 'none';
      const data = await listFleetInsurance({
        page,
        limit: 25,
        status: uninsuredOnly ? 'none' : insStatus,
        search: insSearch || undefined,
        divisionId: insDivisionId || undefined,
        uninsuredOnly,
      });
      setInsItems(data.items || []);
      setInsPages(data.pagination?.totalPages || 1);
      setInsPage(data.pagination?.page || page);
      if (data.tiers?.length) setInsuranceTiers(data.tiers);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to load fleet insurance');
    } finally {
      setInsLoading(false);
    }
  }, [insPage, insStatus, insSearch, insDivisionId, showError]);

  useEffect(() => {
    loadDivisions();
  }, [loadDivisions]);

  useEffect(() => {
    if (tab === 0) loadPasses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, passStatus, passDivisionId]);

  useEffect(() => {
    if (tab === 1) loadInsurance(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, insStatus, insDivisionId]);

  const divisionOptions = useMemo(
    () => (Array.isArray(divisions) ? divisions : [])
      .filter((d) => isValidObjectId(d?._id))
      .map((d) => ({ id: String(d._id), label: d.name })),
    [divisions]
  );

  const searchPassRiders = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setPassRiderOptions([]);
      return;
    }
    try {
      const items = await ridersService.search(q, 15);
      setPassRiderOptions(items);
    } catch {
      setPassRiderOptions([]);
    }
  }, []);

  const openPassDialog = (row = null) => {
    const selectedRider = row
      ? { _id: row.riderId, name: row.name, employeeID: row.employeeID }
      : null;
    setPassRiderOptions(selectedRider ? [selectedRider] : []);
    setPassDialog({
      riderId: row?.riderId || '',
      selectedRider,
      passTypeId: row?.passTypeId || passTypes[0]?.id || '',
      expiresAt: toDateInputValue(row?.expiresAt),
      durationDays: '',
      mode: row ? 'edit' : 'add',
    });
  };

  const openInsDialog = async (row = null) => {
    const rawDivisionId = row?.division?._id || insDivisionId || '';
    const divisionId = isValidObjectId(rawDivisionId) ? String(rawDivisionId) : '';
    let trucks = [];
    if (divisionId) {
      try {
        trucks = await listDivisionTrucks(divisionId);
      } catch {
        trucks = [];
      }
    }
    setInsDialog({
      divisionTruckId: row?.divisionTruckId || '',
      divisionId,
      trucks,
      tierId: row?.tierId || insuranceTiers[0]?.id || '',
      expiresAt: toDateInputValue(row?.expiresAt),
      billingPeriodDays: 30,
      autoRenew: row?.autoRenew ?? true,
      useCustomExpiry: Boolean(row?.expiresAt),
      mode: row ? 'edit' : 'add',
    });
  };

  const onInsDivisionChange = async (divisionId) => {
    const validId = isValidObjectId(divisionId) ? String(divisionId) : '';
    let trucks = [];
    if (validId) {
      try {
        trucks = await listDivisionTrucks(validId);
      } catch {
        trucks = [];
      }
    }
    setInsDialog((p) => ({
      ...p,
      divisionId: validId,
      divisionTruckId: trucks[0]?._id || '',
      trucks,
    }));
  };

  const savePass = async () => {
    const riderId = passDialog?.selectedRider?._id || passDialog?.riderId;
    if (!riderId) {
      showError('Select a rider');
      return;
    }
    setSaving(true);
    try {
      const body = { passTypeId: passDialog.passTypeId };
      if (passDialog.expiresAt) {
        body.expiresAt = new Date(passDialog.expiresAt).toISOString();
      } else if (passDialog.durationDays) {
        body.durationDays = Number(passDialog.durationDays);
      }
      await setDriverPass(String(riderId), body);
      showSuccess('Driver pass updated');
      setPassDialog(null);
      loadPasses(passPage);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to save driver pass');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePass = async (riderId, name) => {
    if (!window.confirm(`Remove driver pass for ${name}?`)) return;
    try {
      await removeDriverPass(riderId);
      showSuccess('Driver pass removed');
      loadPasses(passPage);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to remove pass');
    }
  };

  const saveInsurance = async () => {
    if (!insDialog?.divisionTruckId) {
      showError('Select a truck');
      return;
    }
    if (!insDialog.tierId) {
      showError('Select an insurance tier');
      return;
    }
    setSaving(true);
    try {
      const body = {
        tierId: insDialog.tierId,
        autoRenew: insDialog.autoRenew,
      };
      if (insDialog.useCustomExpiry && insDialog.expiresAt) {
        body.expiresAt = new Date(insDialog.expiresAt).toISOString();
      } else {
        body.billingPeriodDays = Number(insDialog.billingPeriodDays) || 30;
      }
      await setFleetInsurance(insDialog.divisionTruckId, body);
      showSuccess('Fleet insurance updated');
      setInsDialog(null);
      loadInsurance(insPage);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to save insurance');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveInsurance = async (divisionTruckId, label) => {
    if (!window.confirm(`Remove insurance for ${label}?`)) return;
    try {
      await removeFleetInsurance(divisionTruckId);
      showSuccess('Insurance removed');
      loadInsurance(insPage);
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to remove insurance');
    }
  };

  const [bulkForm, setBulkForm] = useState({
    divisionId: '',
    tierId: '',
    billingPeriodDays: 30,
    autoRenew: true,
  });

  const saveBulkInsurance = async () => {
    if (!bulkForm.divisionId || !bulkForm.tierId) {
      showError('Division and tier are required');
      return;
    }
    setSaving(true);
    try {
      const result = await bulkSetDivisionInsurance({
        divisionId: bulkForm.divisionId,
        tierId: bulkForm.tierId,
        billingPeriodDays: Number(bulkForm.billingPeriodDays) || 30,
        autoRenew: bulkForm.autoRenew,
      });
      showSuccess(`Updated ${result.updated} truck(s)${result.failed ? `, ${result.failed} failed` : ''}`);
      setBulkDialog(false);
      loadInsurance(insPage);
    } catch (e) {
      showError(e?.response?.data?.message || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <AdminPageHeader
        title="Passes & insurance registry"
        description="View who holds driver passes and division fleet insurance. Override, add, modify, or remove records without charging wallets."
      />
      <Feedback />

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Driver passes" />
          <Tab label="Fleet insurance" />
        </Tabs>

        {tab === 0 && (
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Per-rider driver passes</Typography>
              <Button size="small" startIcon={<AddOutlined />} variant="contained" onClick={() => openPassDialog()}>
                Grant pass
              </Button>
            </Stack>

            <AdminFilterBar
              search={{
                value: passSearch,
                onChange: setPassSearch,
                placeholder: 'Search rider name, employee ID…',
              }}
              onReset={() => {
                setPassSearch('');
                setPassStatus('all');
                setPassDivisionId('');
                setPassPage(1);
                loadPasses(1);
              }}
            >
              <Button size="small" variant="contained" onClick={() => { setPassPage(1); loadPasses(1); }}>
                Search
              </Button>
              <TextField
                select
                size="small"
                label="Status"
                value={passStatus}
                onChange={(e) => setPassStatus(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                {PASS_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Division"
                value={passDivisionId}
                onChange={(e) => setPassDivisionId(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All divisions</MenuItem>
                {divisionOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                ))}
              </TextField>
            </AdminFilterBar>

            {passLoading && <LinearProgress sx={{ mb: 1 }} />}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rider</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Pass type</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passItems.map((row) => (
                  <TableRow key={row.riderId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                      {row.employeeID && (
                        <Typography variant="caption" color="text.secondary">{row.employeeID}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.division?.name || '—'}</TableCell>
                    <TableCell>{row.passTypeLabel || '—'}</TableCell>
                    <TableCell>
                      {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit pass">
                        <IconButton size="small" onClick={() => openPassDialog(row)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove pass">
                        <IconButton size="small" color="error" onClick={() => handleRemovePass(row.riderId, row.name)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {passItems.length === 0 && !passLoading && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <AdminEmptyState title="No riders match" description="Try different filters or grant a pass." />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {passPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={passPages} page={passPage} onChange={(_, p) => loadPasses(p)} color="primary" />
              </Box>
            )}
          </CardContent>
        )}

        {tab === 1 && (
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Fleet insurance (per division truck)</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => {
                  setBulkForm({
                    divisionId: insDivisionId || '',
                    tierId: insuranceTiers[0]?.id || '',
                    billingPeriodDays: 30,
                    autoRenew: true,
                  });
                  setBulkDialog(true);
                }}>
                  Bulk assign division
                </Button>
                <Button size="small" startIcon={<AddOutlined />} variant="contained" onClick={() => openInsDialog()}>
                  Assign insurance
                </Button>
              </Stack>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
              Fleet insurance is assigned per division truck (not per rider or per division as a whole).
              Each truck can have its own tier and expiry. Use bulk assign to cover all active trucks in a division at once.
            </Alert>

            <AdminFilterBar
              search={{
                value: insSearch,
                onChange: setInsSearch,
                placeholder: 'Search division or truck…',
              }}
              onReset={() => {
                setInsSearch('');
                setInsStatus('all');
                setInsDivisionId('');
                setInsPage(1);
                loadInsurance(1);
              }}
            >
              <Button size="small" variant="contained" onClick={() => { setInsPage(1); loadInsurance(1); }}>
                Search
              </Button>
              <TextField
                select
                size="small"
                label="Status"
                value={insStatus}
                onChange={(e) => setInsStatus(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {INS_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Division"
                value={insDivisionId}
                onChange={(e) => setInsDivisionId(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All divisions</MenuItem>
                {divisionOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                ))}
              </TextField>
            </AdminFilterBar>

            {insLoading && <LinearProgress sx={{ mb: 1 }} />}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Division</TableCell>
                  <TableCell>Division truck</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Auto-renew</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {insItems.map((row) => (
                  <TableRow key={String(row.divisionTruckId)} hover>
                    <TableCell>{row.division?.name || '—'}</TableCell>
                    <TableCell>{row.truck?.displayName || '—'}</TableCell>
                    <TableCell>{row.tierLabel || '—'}</TableCell>
                    <TableCell>
                      {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      {row.status === 'none' ? '—' : (
                        <Chip size="small" label={row.autoRenew ? 'On' : 'Off'} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{statusChip(row.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={row.status === 'none' ? 'Assign insurance' : 'Edit insurance'}>
                        <IconButton size="small" onClick={() => openInsDialog(row)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {row.status !== 'none' && (
                        <Tooltip title="Remove insurance">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveInsurance(row.divisionTruckId, row.truck?.displayName)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {insItems.length === 0 && !insLoading && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ border: 0 }}>
                      <AdminEmptyState title="No records match" description="Try different filters or assign insurance." />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {insPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={insPages} page={insPage} onChange={(_, p) => loadInsurance(p)} color="primary" />
              </Box>
            )}
          </CardContent>
        )}
      </Card>

      <Dialog open={Boolean(passDialog)} onClose={() => setPassDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{passDialog?.mode === 'edit' ? 'Edit driver pass' : 'Grant driver pass'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {passDialog?.mode === 'edit' ? (
              <TextField
                label="Rider"
                value={passDialog?.selectedRider?.name || ''}
                disabled
                fullWidth
                size="small"
                helperText={passDialog?.selectedRider?.employeeID || ''}
              />
            ) : (
              <Autocomplete
                options={passRiderOptions}
                getOptionLabel={(o) => `${o.name || o.tmpIngameName || 'Rider'}${o.employeeID ? ` (${o.employeeID})` : ''}`}
                value={passDialog?.selectedRider || null}
                onChange={(_, rider) => {
                  setPassDialog((p) => ({
                    ...p,
                    selectedRider: rider,
                    riderId: rider?._id || '',
                  }));
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') searchPassRiders(value);
                }}
                isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rider"
                    placeholder="Search by name or employee ID"
                    size="small"
                    helperText="Type at least 2 characters to search"
                  />
                )}
              />
            )}
            <TextField
              select
              label="Pass type"
              value={passDialog?.passTypeId || ''}
              onChange={(e) => setPassDialog((p) => ({ ...p, passTypeId: e.target.value }))}
              fullWidth
              size="small"
            >
              {passTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label} ({t.durationDays}d)</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Custom expiry date"
              type="date"
              value={passDialog?.expiresAt || ''}
              onChange={(e) => setPassDialog((p) => ({ ...p, expiresAt: e.target.value, durationDays: '' }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              helperText="Leave empty to extend by duration below"
            />
            <TextField
              label="Or duration (days from now / stack)"
              type="number"
              value={passDialog?.durationDays || ''}
              onChange={(e) => setPassDialog((p) => ({ ...p, durationDays: e.target.value, expiresAt: '' }))}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPassDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={savePass} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(insDialog)} onClose={() => setInsDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{insDialog?.mode === 'edit' ? 'Edit fleet insurance' : 'Assign fleet insurance'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Division"
              value={insDialog?.divisionId || ''}
              onChange={(e) => onInsDivisionChange(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">Select division</MenuItem>
              {divisionOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Division truck"
              value={insDialog?.divisionTruckId || ''}
              onChange={(e) => setInsDialog((p) => ({ ...p, divisionTruckId: e.target.value }))}
              fullWidth
              size="small"
              disabled={!insDialog?.trucks?.length}
              helperText={insDialog?.trucks?.length ? 'Insurance applies to this truck only' : 'Select a division first'}
            >
              {(insDialog?.trucks || []).map((t) => (
                <MenuItem key={t._id} value={t._id}>{t.displayName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Insurance tier"
              value={insDialog?.tierId || ''}
              onChange={(e) => setInsDialog((p) => ({ ...p, tierId: e.target.value }))}
              fullWidth
              size="small"
            >
              {insuranceTiers.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(insDialog?.useCustomExpiry)}
                  onChange={(e) => setInsDialog((p) => ({ ...p, useCustomExpiry: e.target.checked }))}
                />
              }
              label="Set custom expiry date"
            />
            {insDialog?.useCustomExpiry ? (
              <TextField
                label="Expiry date"
                type="date"
                value={insDialog?.expiresAt || ''}
                onChange={(e) => setInsDialog((p) => ({ ...p, expiresAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            ) : (
              <TextField
                label="Billing period (days)"
                type="number"
                value={insDialog?.billingPeriodDays ?? 30}
                onChange={(e) => setInsDialog((p) => ({ ...p, billingPeriodDays: e.target.value }))}
                fullWidth
                size="small"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(insDialog?.autoRenew)}
                  onChange={(e) => setInsDialog((p) => ({ ...p, autoRenew: e.target.checked }))}
                />
              }
              label="Auto-renew"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveInsurance} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk assign division insurance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Division"
              value={bulkForm.divisionId}
              onChange={(e) => setBulkForm((p) => ({ ...p, divisionId: e.target.value }))}
              fullWidth
              size="small"
            >
              <MenuItem value="">Select division</MenuItem>
              {divisionOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Insurance tier"
              value={bulkForm.tierId}
              onChange={(e) => setBulkForm((p) => ({ ...p, tierId: e.target.value }))}
              fullWidth
              size="small"
            >
              {insuranceTiers.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Billing period (days)"
              type="number"
              value={bulkForm.billingPeriodDays}
              onChange={(e) => setBulkForm((p) => ({ ...p, billingPeriodDays: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={bulkForm.autoRenew}
                  onChange={(e) => setBulkForm((p) => ({ ...p, autoRenew: e.target.checked }))}
                />
              }
              label="Auto-renew"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBulkInsurance} disabled={saving}>
            {saving ? 'Applying…' : 'Apply to all trucks'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
